import { useAuthPages, authPageFailureText, authResponseFailure, type AuthPageFailure } from "../i18n/authPagesPresentation";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Alert } from "../components/ui/Alert";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";

export const ResetPasswordPage = () => {
  const t = useAuthPages();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: publicConfig } = usePublicConfig();
  const branding = useBrandingSettings();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AuthPageFailure | null>(null);
  const [mismatch, setMismatch] = useState(false);

  // No token — redirect to forgot password
  if (!token) {
    return (
      <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <AuthBrandingHeader
            title={t($ => $.invalidLink)}
            subtitle={t($ => $.invalidLinkSubtitle)}
            authPages={publicConfig?.authPages}
            branding={branding.settings}
          />
          <Card className="glass-card">
            <Alert variant="warning" className="mb-4">
              {t($ => $.missingToken)}
            </Alert>
            <Link to="/forgot-password">
              <Button type="button" size="lg" className="w-full">
                {t($ => $.requestNewLink)}
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMismatch(false);
    setError(null);

    if (password !== confirmPassword) {
      setMismatch(true);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(authResponseFailure(data?.presentation, data?.message, "resetFailed"));
        return;
      }
      navigate("/login?reset=success", { replace: true });
    } catch {
      setError({ key: "network" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title={t($ => $.resetTitle)}
          subtitle={t($ => $.resetSubtitle)}
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t($ => $.resetHeading)}</h2>
              <p className="text-sm text-muted-foreground">{t($ => $.resetDescription)}</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
              {authPageFailureText(error, t)}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.newPassword)}</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.confirmPassword)}</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setMismatch(false);
                }}
                autoComplete="new-password"
                required
              />
              {mismatch && (
                <p className="text-xs text-destructive-text">{t($ => $.passwordMismatch)}</p>
              )}
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? t($ => $.resetting) : t($ => $.resetHeading)}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            {t($ => $.rememberPassword)} <Link to="/login">{t($ => $.signIn)}</Link>
          </p>
        </Card>
      </div>
    </div>
  );
};
